import React, { useState } from 'react';
import {
  IAuditoriaReceitasDiferidasResultado,
  IItemReceitasDiferidas,
} from '../interfaces/IAuditoriaReceitasDiferidas';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaReceitasDiferidasResultado;
}



const formatPercent = (value?: number) =>
  value !== undefined ? `${(value * 100).toFixed(2).replace('.', ',')}%` : '-';

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-283: drill-down a nível de lançamento — todo AVISO/ERRO precisa mostrar não só o saldo
// agregado, mas os lançamentos reais (créditos de entrada + débitos de baixa/NF) que o formam.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemReceitasDiferidas }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaReceitasDiferidasDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const receitasDiferidasPendentes = resultado.receitasDiferidasPendentes || [];
  const receitasDiferidasConciliadas = resultado.receitasDiferidasConciliadas || [];
  const avisosEmissaoNf = receitasDiferidasPendentes.filter((item) => item.status === 'AVISO');
  const errosCustoOmissao = receitasDiferidasPendentes.filter((item) => item.status === 'ERRO');

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Receitas Diferidas (Segregação Tributária) — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Custo de Omissão (≥ 60 dias): PIS + COFINS + IRPJ (+ adicional no Real) + CSLL + ISS (se aplicável) + Multa ECD Potencial (5% limitada a 1% da receita bruta do período).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>31-59 dias sem baixa = Aviso (emissão de NF). 60+ dias = Erro (Custo de Omissão).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal e matriz de cargas aplicada à segregação.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Segregação Tributária de Receitas Diferidas:</strong> {resultado.fundamentacaoSegregacao}</div>
        </div>
      )}

      {/* Avisos de Compliance (31 a 59 dias sem NF emitida) — sem cálculo de tributos */}
      <div className='auditoria-card'>
        <h3>Aviso de Compliance — Emissão de NF (31 a 59 dias sem baixa)</h3>
        <p className='text-muted'>
          Receita diferida (adiantamento de cliente) sem baixa entre 31 e 59 dias. Notificação de emissão obrigatória
          de Nota Fiscal de Serviços — sem cálculo de tributos nesta fase.
        </p>

        {avisosEmissaoNf.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma receita diferida entre 31 e 59 dias sem baixa identificada no período.</div>
        )}

        {avisosEmissaoNf.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-center'>Dias sem baixa</th>
                  <th className='text-end'>Saldo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {avisosEmissaoNf.map((item: IItemReceitasDiferidas, idx) => {
                  const chave = `ra-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-aviso' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td className='text-center'>{item.diasEstagnado}</td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Erro de Conformidade (>= 60 dias sem NF emitida) — Custo de Omissão, tributação integral */}
      <div className='auditoria-card'>
        <h3>Erro de Conformidade — Custo de Omissão (≥ 60 dias sem NF emitida)</h3>
        <p className='text-muted'>
          Receita diferida sem baixa há 60 dias ou mais. Custo de Omissão aplicado — tributação integral exigível
          conforme regime fiscal (Real/Presumido) + 5% ISS (se aplicável) + Multa ECD potencial (5%
          limitada a 1% da receita bruta do período) por erro/omissão na escrituração.
        </p>

        {errosCustoOmissao.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma receita diferida com 60 dias ou mais sem baixa identificada no período.</div>
        )}

        {errosCustoOmissao.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-end'>Saldo Estagnado</th>
                  <th className='text-end'>PIS</th>
                  <th className='text-end'>COFINS</th>
                  <th className='text-end'>IRPJ</th>
                  <th className='text-end'>CSLL</th>
                  <th className='text-end'>ISS</th>
                  <th className='text-end'>Multa ECD</th>
                  <th className='text-end'>Custo de Omissão</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {errosCustoOmissao.map((item: IItemReceitasDiferidas, idx) => {
                  const chave = `rp-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-erro' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.regimeTributario || 'Regime não identificado'} · {item.diasEstagnado} dias sem baixa</div>
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td className='text-end'>{formatCurrency(item.valorPis)}</td>
                        <td className='text-end'>{formatCurrency(item.valorCofins)}</td>
                        <td className='text-end'>{formatCurrency(item.valorIrpj)}</td>
                        <td className='text-end'>{formatCurrency(item.valorCsll)}</td>
                        <td className='text-end'>{item.incideIss ? formatCurrency(item.valorIss) : formatCurrency(0)}</td>
                        <td className='text-end'>{formatCurrency(item.valorMultaEcd)}</td>
                        <td className='text-end auditoria-col-highlight'>{formatCurrency(item.impactoFiscalTotal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={10} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                                PIS: {formatPercent(item.aliquotaPis)} · COFINS: {formatPercent(item.aliquotaCofins)} ·
                                {' '}IRPJ: {formatPercent(item.aliquotaIrpj)}
                                {item.valorAdicionalIrpj > 0 && ` (+ adicional ${formatPercent(item.aliquotaAdicionalIrpj)} sobre excedente: ${formatCurrency(item.valorAdicionalIrpj)})`} ·
                                {' '}CSLL: {formatPercent(item.aliquotaCsll)} ·
                                {' '}ISS: {item.incideIss ? formatPercent(item.aliquotaIss) : 'não incide (sem receita de serviço no período)'} ·
                                {' '}Multa ECD: {formatPercent(item.aliquotaMultaEcd)}
                              </div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Receitas Diferidas Conciliadas — visibilidade de que a auditoria rodou */}
      {receitasDiferidasConciliadas.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Receitas Diferidas Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Contas de receita diferida que foram auditadas e estão em conformidade — saldo zerado ou NF emitida dentro de 30 dias.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Último Crédito</th>
                  <th className='text-center'>Dias</th>
                  <th className='text-end'>Saldo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {receitasDiferidasConciliadas.map((item: IItemReceitasDiferidas, idx) => {
                  const chave = `rc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.dataUltimoCredito ? formatDate(item.dataUltimoCredito) : '-'}</td>
                        <td className='text-center'>{item.diasEstagnado || '-'}</td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaReceitasDiferidasDetalhe;
